/**
 * Polyhedra Geometry Node Definitions
 *
 * Generates geometry nodes from polyhedra configs.
 * Use the FaceColours node to add face-type coloring.
 */

import type { BufferGeometry } from 'three';
import { BaseNode } from '@nodes/BaseNode';

import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../../threejs/threejsPlugin';
import { polyhedraConfigs, type PolyhedraConfig } from './polyhedraConfigs';
import { logger } from '@/logging';
import { internals, params } from '@/nodes/TypedEndpoints';

type ThreeModule = typeof import('three');

const solidOptions = polyhedraConfigs.map(config => config.type);


// Standard endpoints that all geometries have
const endpoints = {
  ...params({
    type: { dataType: 'string', options: solidOptions, value: solidOptions[0] },
    radius: { dataType: 'number', value: 1, min: 0.1, max: 100, step: 0.1 },
  }),
  ...internals({
    geometryOut: {
      dataType: 'three:geometry',
      isOutput: true
    }
  })

};

/**
 * Polyhedra geometry node - creates polyhedra from config
 */
class SolidNode extends BaseNode<typeof endpoints> {
  protected three: ThreeModule;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  initialize(): void {
    super.initialize();
    this.buildGeometry();
  }

  protected buildGeometry(): void {
    if (!this.three) return;



    const config = polyhedraConfigs.find(c => c.type === this.params.type.value)

    logger.trace(this.params.type.value, config.vertices, config.indices)

    const { vertices, indices } = config

    const geometry = new this.three.PolyhedronGeometry(
      vertices,
      indices,
      this.params.radius.value,
      0
    );

    this.internals.geometryOut.set(geometry);
  }



  onParamChanged(_payload: any, _endpointId: string): void {
    this.buildGeometry();
  }


}

// ─────────────────────────────────────────────────────────────
// Generate NodeDefs from configs
// ─────────────────────────────────────────────────────────────

export const solidNode: NodeDef = {
  type: 'Solid',
  shortName: 'solid',
  keywords: ['geometry'],
  endpoints,
  ctor: SolidNode
}
